import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import { useNotificationStore } from '@store/notificationStore';
import type {
  AgentMemoryConsolidateResponse,
  AgentMemoryObservation,
  AgentMemoryObservationListResponse,
  AgentMemoryObservationStatus,
  AgentMemoryScope,
  AgentMemoryVisibilityConfig,
  PolicyReviewDecision,
  PolicyReviewDecisionResponse,
  PolicyReviewListResponse,
  PolicyV2ListResponse,
} from '@schemas/api';

const memoryKeys = {
  all: ['memories'] as const,
  access: () => [...memoryKeys.all, 'access'] as const,
  observations: (
    scope: AgentMemoryScope | undefined,
    status: AgentMemoryObservationStatus | undefined,
    search: string
  ) => [...memoryKeys.all, 'observations', scope ?? 'effective', status ?? 'staged', search] as const,
  visibility: () => [...memoryKeys.all, 'visibility'] as const,
  policies: (status?: string) => [...memoryKeys.all, 'policies', status ?? 'all'] as const,
  reviews: (status?: string) => [...memoryKeys.all, 'policy-reviews', status ?? 'open'] as const,
};

export const usePolicies = (status?: string, enabled = true) => useQuery({
  queryKey: memoryKeys.policies(status),
  queryFn: async () => (await apiClient.get<PolicyV2ListResponse>('/memories/policies', {
    params: { status: status || undefined, limit: 200 },
  })).data,
  enabled,
});

export const usePolicyReviews = (status = 'open', enabled = true) => useQuery({
  queryKey: memoryKeys.reviews(status),
  queryFn: async () => (await apiClient.get<PolicyReviewListResponse>('/memories/policy-reviews', {
    params: { status, limit: 200 },
  })).data,
  enabled,
});

export const useDecidePolicyReview = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async ({ reviewId, decision, expectedVersion, reason }: {
      reviewId: string;
      decision: PolicyReviewDecision;
      expectedVersion: number;
      reason?: string;
    }) => (await apiClient.post<PolicyReviewDecisionResponse>(
      `/memories/policy-reviews/${reviewId}/decision`,
      { decision, expected_version: expectedVersion, reason: reason ?? '' },
    )).data,
    onSuccess: () => {
      addNotification({ message: 'Policy review updated', type: 'success' });
      queryClient.invalidateQueries({ queryKey: memoryKeys.all });
    },
    onError: (error: AxiosError<{ detail?: string }>) => addNotification({
      message: extractErrorMessage(error, 'Failed to update policy review'),
      type: 'error',
    }),
  });
};

const extractErrorMessage = (error: AxiosError<{ detail?: string }>, fallback: string) =>
  error.response?.data?.detail || fallback;

export const useMemoryAccess = () => {
  return useQuery({
    queryKey: memoryKeys.access(),
    queryFn: async () => {
      const { data } = await apiClient.get<AgentMemoryVisibilityConfig>('/memories/access');
      return data;
    },
  });
};

export const useMemories = (
  scope?: AgentMemoryScope,
  status: AgentMemoryObservationStatus = 'staged',
  search = '',
  enabled = true,
) => {
  return useQuery({
    queryKey: memoryKeys.observations(scope, status, search),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (scope) params.set('scope', scope);
      if (status) params.set('status', status);
      params.set('limit', '100');
      const { data } = await apiClient.get<AgentMemoryObservationListResponse>(
        `/memories/observations?${params.toString()}`
      );
      const normalizedSearch = search.trim().toLowerCase();
      if (!normalizedSearch) return data;
      return {
        ...data,
        items: data.items.filter((item) => [
          item.content,
          item.rule_key,
          item.rule_value,
          ...Object.values(item.reference_labels ?? {}),
        ].some((value) => value?.toLowerCase().includes(normalizedSearch))),
      };
    },
    enabled,
  });
};

export const useConsolidateMemory = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (memoryId: string) => {
      const { data } = await apiClient.post<AgentMemoryConsolidateResponse>(
        `/memories/${memoryId}/consolidate`
      );
      return data;
    },
    onSuccess: (result) => {
      addNotification({
        message: result.status === 'conflicted'
          ? 'Memory was marked conflicted and was not activated'
          : 'Memory consolidated and activated',
        type: result.status === 'conflicted' ? 'warning' : 'success',
      });
      queryClient.invalidateQueries({ queryKey: memoryKeys.all });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: extractErrorMessage(error, 'Failed to consolidate memory'),
        type: 'error',
      });
    },
  });
};

export const useMemoryVisibilityConfig = () => {
  return useQuery({
    queryKey: memoryKeys.visibility(),
    queryFn: async () => {
      const { data } = await apiClient.get<AgentMemoryVisibilityConfig>('/memories/visibility');
      return data;
    },
  });
};

export const useUpdateMemoryVisibilityConfig = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (tenantMemoryRoleIds: string[]) => {
      const { data } = await apiClient.put<AgentMemoryVisibilityConfig>('/memories/visibility', {
        tenant_memory_role_ids: tenantMemoryRoleIds,
      });
      return data;
    },
    onSuccess: () => {
      addNotification({ message: 'Memory visibility updated', type: 'success' });
      queryClient.invalidateQueries({ queryKey: memoryKeys.all });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: extractErrorMessage(error, 'Failed to update memory visibility'),
        type: 'error',
      });
    },
  });
};
